export type Passkey = {
  id: string
  nickname: string | null
  createdAt: string
  lastUsedAt: string | null
}

/**
 * Signing in with Face ID, a fingerprint or a security key (D-320).
 *
 * **The browser does the interesting part.** `navigator.credentials` talks to
 * the phone's secure element; nothing here ever sees a private key, and there is
 * nothing in this file worth stealing.
 *
 * **Two encodings, and mixing them up is the classic mistake.** WebAuthn puts
 * `ArrayBuffer`s where JSON needs strings, and the strings are **base64url** —
 * `-` and `_` rather than `+` and `/`, and no padding. Plain `btoa` produces
 * base64, which the server rejects as a different credential entirely.
 */

/**
 * Whether this deployment has passkeys configured (D-320).
 *
 * **Separate from browser support, and both must be true.** The relying-party
 * domain defaults to localhost so development needs no configuration — which
 * means a deployed host without the setting would offer a button that the
 * browser refuses with a message no parent can act on. Asking first is the
 * difference between "not available here" and "something is broken".
 */
export async function passkeysAvailable(): Promise<boolean> {
  if (!passkeysSupported()) return false

  try {
    const res = await fetch('/api/auth/passkeys/available')
    if (!res.ok) return false

    const body = (await res.json()) as { available?: boolean }
    return body.available === true
  } catch {
    // A failed lookup is not a statement about the data (D-209): if we cannot
    // tell, do not offer it.
    return false
  }
}

/** Whether this browser can do passkeys at all. */
export function passkeysSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential !== 'undefined' &&
    typeof navigator?.credentials?.create === 'function'
  )
}

function fromBase64Url(value: string): ArrayBuffer {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/')
  const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4))
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0))

  /*
   * The buffer rather than the view, because that is what the DOM types ask
   * for - a Uint8Array is backed by ArrayBufferLike, which also admits a
   * SharedArrayBuffer and so will not narrow. Slicing gives a plain one.
   */
  return bytes.buffer.slice(0) as ArrayBuffer
}

function toBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''

  // Chunked rather than spread: a security key's attestation object runs to
  // several kilobytes, and `String.fromCharCode(...bytes)` blows the argument
  // limit somewhere above about 64k with a RangeError nobody can read.
  for (let i = 0; i < bytes.length; i += 8192) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 8192))
  }

  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/**
 * Decodes the ids on a credential list, leaving everything else alone.
 *
 * The server sends `type` and `transports` too, and dropping them would throw
 * away the hints that let a browser say "use the key you plugged in" rather
 * than offering everything.
 */
function descriptors(list: unknown): PublicKeyCredentialDescriptor[] {
  return ((list ?? []) as { id: string; type: string; transports?: string[] }[]).map((c) => ({
    ...c,
    type: 'public-key' as const,
    transports: c.transports as AuthenticatorTransport[] | undefined,
    id: fromBase64Url(c.id),
  }))
}

async function send(url: string, body: unknown): Promise<Record<string, unknown>> {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })

  if (!res.ok) {
    // Every refusal here is a written sentence — "start again", "not on a
    // shared tablet". Replacing them with a status code loses the only useful
    // part.
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.detail ?? problem?.title ?? `That did not work (${res.status})`)
  }

  return res.json()
}

/**
 * Sets one up on this device.
 *
 * **Throws what the browser says.** A person who taps Cancel on the Face ID
 * prompt gets `NotAllowedError`, which is not a fault and must not be reported
 * as one — the caller checks for it.
 */
export async function registerPasskey(nickname: string | null): Promise<void> {
  const begun = await send('/api/auth/passkeys/register/begin', {})
  const options = begun.options as Record<string, never>

  const created = (await navigator.credentials.create({
    publicKey: {
      ...(options as unknown as PublicKeyCredentialCreationOptions),
      challenge: fromBase64Url(options.challenge as unknown as string),
      user: {
        ...(options.user as unknown as PublicKeyCredentialUserEntity),
        id: fromBase64Url((options.user as unknown as { id: string }).id),
      },
      excludeCredentials: descriptors(options.excludeCredentials),
    },
  })) as PublicKeyCredential | null

  if (!created) throw new Error('No passkey was created.')

  const response = created.response as AuthenticatorAttestationResponse

  await send('/api/auth/passkeys/register/complete', {
    handle: begun.handle,
    nickname,
    response: {
      id: created.id,
      rawId: toBase64Url(created.rawId),
      type: created.type,
      response: {
        attestationObject: toBase64Url(response.attestationObject),
        clientDataJSON: toBase64Url(response.clientDataJSON),
      },
      clientExtensionResults: created.getClientExtensionResults(),
    },
  })
}

/**
 * Signs in with one.
 *
 * `email` is optional and usually omitted: with no allow-list the browser offers
 * whatever it holds for this site, so she taps once and types nothing.
 */
export async function signInWithPasskey(email?: string): Promise<void> {
  const begun = await send('/api/auth/passkeys/signin/begin', { email: email ?? null })
  const options = begun.options as Record<string, never>

  const got = (await navigator.credentials.get({
    publicKey: {
      ...(options as unknown as PublicKeyCredentialRequestOptions),
      challenge: fromBase64Url(options.challenge as unknown as string),
      allowCredentials: descriptors(options.allowCredentials),
    },
  })) as PublicKeyCredential | null

  if (!got) throw new Error('No passkey was offered.')

  const response = got.response as AuthenticatorAssertionResponse

  await send('/api/auth/passkeys/signin/complete', {
    handle: begun.handle,
    response: {
      id: got.id,
      rawId: toBase64Url(got.rawId),
      type: got.type,
      response: {
        authenticatorData: toBase64Url(response.authenticatorData),
        signature: toBase64Url(response.signature),
        clientDataJSON: toBase64Url(response.clientDataJSON),
        userHandle: response.userHandle ? toBase64Url(response.userHandle) : null,
      },
      clientExtensionResults: got.getClientExtensionResults(),
    },
  })
}

export async function fetchPasskeys(): Promise<Passkey[]> {
  const res = await fetch('/api/auth/passkeys')
  if (!res.ok) throw new Error(`Could not load your passkeys (${res.status})`)
  return res.json()
}

export async function removePasskey(id: string): Promise<void> {
  const res = await fetch(`/api/auth/passkeys/${id}`, { method: 'DELETE' })
  if (!res.ok) throw new Error(`Could not remove it (${res.status})`)
}

/**
 * Whether this was somebody changing their mind.
 *
 * Tapping Cancel on a Face ID prompt raises `NotAllowedError`, and so does a
 * timeout. Neither is a fault, and showing red for one teaches people the app
 * is broken when it did exactly what they asked.
 */
export function wasCancelled(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'NotAllowedError'
}
